import { Link, NavLink, Outlet, useMatch } from "react-router-dom";
import { Obs, useObserver, useSubtree } from "subtree.js/react";
import { hasCapability } from "../core/primitives/capabilities";
import { can } from "../core/primitives/store-role";
import { workspaceLabel } from "../core/primitives/workspace";
import { SetupChecklist } from "../sections/setup-checklist/setup-checklist";
import { ShellActions, ShellState } from "../shell/shell-model";

/**
 * The count of carts waiting on the merchant. Accent-filled, because this is the
 * one number in the shell that means "someone is standing at your counter".
 */
function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-ink">
      {count}
    </span>
  );
}

const navCls = ({ isActive }: { isActive: boolean }) =>
  `block rounded-btn px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive ? "bg-surface text-accent" : "text-muted hover:bg-surface hover:text-white"
  }`;

function LockIcon() {
  return (
    <svg aria-hidden="true" focusable="false" className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 2a4 4 0 0 0-4 4v2H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-1V6a4 4 0 0 0-4-4Zm2 6V6a2 2 0 1 0-4 0v2h4Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/** A feature HobbyScan has not switched on: still listed, so it can be found, and its page asks for it. */
function LockedLabel({ children }: { children: string }) {
  return (
    <span className="flex items-center justify-between gap-2 opacity-60" title="Not switched on for this shop">
      {children}
      <LockIcon />
      <span className="sr-only"> (not switched on)</span>
    </span>
  );
}

/** The shop × location the app is working at, linking to the shop's settings. */
function ShopIdentity({ onNavigate }: { onNavigate?: () => void }) {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const selectedStore = observer.watch(state.selectedStore);
  const location = observer.watch(state.selectedLocation);
  const role = observer.watch(state.role);
  if (!selectedStore) return null;

  const name = workspaceLabel(selectedStore, location);
  const initial = (selectedStore.name ?? "Your shop").charAt(0).toUpperCase();
  const badge = selectedStore.logoUrl ? (
    <img
      src={selectedStore.logoUrl}
      alt=""
      className="h-6 w-6 shrink-0 rounded-thumb border border-line object-cover"
    />
  ) : (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-thumb bg-accent text-[11px] font-semibold text-ink">
      {initial}
    </span>
  );

  // A Staff member has nowhere this badge could send them, so it stops being a link.
  if (role === "STAFF") {
    return (
      <span className="flex min-w-0 items-center gap-2 rounded-btn px-2 py-1.5" title={name}>
        {badge}
        <span className="truncate text-sm font-medium text-white">{name}</span>
      </span>
    );
  }

  const to =
    role === "MANAGER" ? `/stores/${selectedStore.id}/settings?tab=admins` : `/stores/${selectedStore.id}/settings`;

  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="flex min-w-0 items-center gap-2 rounded-btn px-2 py-1.5 transition-colors hover:bg-surface"
      title={`${name} — settings`}
    >
      {badge}
      <span className="truncate text-sm font-medium text-white">{name}</span>
    </Link>
  );
}

function SwitchWorkspaceButton() {
  const state = useSubtree(ShellState);
  const actions = useSubtree(ShellActions);
  const observer = useObserver();
  if (!observer.watch(state.canSwitchWorkspace)) return null;
  return (
    <button
      type="button"
      onClick={actions.switchWorkspace}
      className="shrink-0 rounded-btn px-2 py-2 text-[13px] font-medium text-muted transition-colors hover:bg-surface hover:text-white"
    >
      Switch
    </button>
  );
}

/**
 * Live, from anywhere in the app. Two different facts share this one control:
 * **on air** is this device's camera publishing; **live** is the shop having a
 * show running, which can be a co-host on another device entirely.
 */
function LiveTopBarButton() {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const onAir = observer.watch(state.onAir);
  const liveNow = observer.watch(state.liveNow);
  const role = observer.watch(state.role);
  const capabilities = observer.watch(state.capabilities);
  const lit = onAir || liveNow;

  // A null role means the shop has not resolved yet; treated as hosting-capable
  // rather than flashing the button on and off once the real role arrives.
  if (role !== null && !can(role, "hostLive")) return null;
  // Switched off, the nav's locked entry is the way in; here it would only be noise.
  if (hasCapability(capabilities, "LIVE_BREAKS") === false && !onAir) return null;

  return (
    <Link
      to="/live"
      className={`flex shrink-0 items-center gap-1.5 rounded-btn px-2.5 py-2 text-sm font-medium transition-colors ${
        lit ? "text-danger hover:bg-surface" : "text-muted hover:bg-surface hover:text-white"
      }`}
      title={onAir ? "You are on air" : liveNow ? "A show is live in your shop" : "Live breaks"}
    >
      {lit && (
        // Not colour alone: the dot pulses, so the state survives a bright room and colour blindness.
        <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-current" />
      )}
      Live
      {lit && <span className="sr-only">{onAir ? " — you are on air" : " — a show is live"}</span>}
    </Link>
  );
}

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const selectedStore = observer.watch(state.selectedStore);
  const onAir = observer.watch(state.onAir);
  const pendingCustomerCarts = observer.watch(state.pendingCarts);
  const role = observer.watch(state.role);
  const capabilities = observer.watch(state.capabilities);
  // A null role means the shop has not resolved yet; treated as hosting-capable
  // rather than flashing Live/Settings on and off once the real role arrives.
  const canHostLive = role === null || can(role, "hostLive");
  const showSettings = role !== "STAFF";
  // Unknown capabilities read as on, for the same reason.
  const posLocked = hasCapability(capabilities, "POINT_OF_SALE") === false;
  const liveLocked = hasCapability(capabilities, "LIVE_BREAKS") === false;
  // One entry for the counter; its page switches between scans and carts.
  const onCarts = useMatch("/customer-carts/*") !== null;
  // Stock and review are one Inventory section, as on iOS.
  const onReview = useMatch("/drafts/*") !== null;

  return (
    <>
      <NavLink
        to="/customer-scans"
        className={({ isActive }) => navCls({ isActive: isActive || onCarts })}
        onClick={onNavigate}
      >
        {posLocked ? (
          <LockedLabel>Customer</LockedLabel>
        ) : (
          <span className="flex items-center">
            Customer
            <NavBadge count={pendingCustomerCarts} />
          </span>
        )}
      </NavLink>

      {/* Everything above is the customer's side of the counter. Below is the shop's own. */}
      <p className="eyebrow px-3 pb-1 pt-4">Shop</p>

      <NavLink
        to="/cards"
        className={({ isActive }) => navCls({ isActive: isActive || onReview })}
        onClick={onNavigate}
      >
        Inventory
      </NavLink>
      <NavLink to="/sealed" className={navCls} onClick={onNavigate}>
        Sealed product
      </NavLink>
      <NavLink to="/events" className={navCls} onClick={onNavigate}>
        Shows
      </NavLink>
      {/* Not `end`: /live/start and /live/shows/:id are all this section. */}
      {canHostLive && (
        <>
          <NavLink to="/live" className={navCls} onClick={onNavigate}>
            {liveLocked ? (
              <LockedLabel>Live breaks</LockedLabel>
            ) : (
              <span className="flex items-center justify-between gap-2">
                Live breaks
                {onAir && (
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-danger">
                    <span className="size-1.5 animate-pulse rounded-full bg-current" />
                    On air
                  </span>
                )}
              </span>
            )}
          </NavLink>
          <NavLink to="/shipping" className={navCls} onClick={onNavigate}>
            Shipping
          </NavLink>
        </>
      )}
      {selectedStore && showSettings && (
        <>
          <hr className="my-2 border-line" />
          <NavLink to={`/stores/${selectedStore.id}/settings`} className={navCls} onClick={onNavigate}>
            Settings
          </NavLink>
        </>
      )}
    </>
  );
}

export function AppLayout() {
  const state = useSubtree(ShellState);
  const actions = useSubtree(ShellActions);
  const observer = useObserver();
  const mobileMenuOpen = observer.watch(state.mobileMenuOpen);
  // The on-air bar is fixed to the top of the viewport, from above the router.
  // Without this the shell keeps its own top edge there and the bar covers the header.
  const onAir = observer.watch(state.onAir);
  // Settings is where the same connection is shown in full.
  const onSettings = useMatch("/stores/:storeId/settings") !== null;

  return (
    <div className={`flex min-h-screen flex-col bg-app ${onAir ? "pt-10" : ""}`}>
      <header className="relative z-20 flex h-16 shrink-0 items-center border-b border-line bg-app-elevated px-4 sm:px-6">
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={actions.toggleMobileMenu}
              className="rounded-btn p-1.5 text-muted transition-colors hover:bg-surface hover:text-white sm:hidden"
              aria-label="Toggle menu"
            >
              <svg
                aria-hidden="true"
                focusable="false"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            {/* The wordmark carries the accent on "Scan", which is how the iOS app writes it. */}
            <span className="hidden text-lg font-bold text-white sm:inline">
              Hobby<span className="text-accent">Scan</span>.Pro
            </span>
            <LiveTopBarButton />
          </div>

          <div className="flex min-w-0 items-center gap-1">
            <ShopIdentity />
            <SwitchWorkspaceButton />
            <Obs>
              {(ref) => {
                const signOut = ref.disableUntilCompleted(actions.signOut);
                return (
                  <button
                    type="button"
                    onClick={signOut}
                    disabled={signOut === undefined}
                    className="shrink-0 rounded-btn px-3 py-2 text-sm font-medium text-muted transition-colors hover:bg-surface hover:text-white"
                  >
                    Log out
                  </button>
                );
              }}
            </Obs>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="z-10 border-b border-line bg-app-elevated sm:hidden">
          <nav className="space-y-0.5 p-3">
            <NavLinks onNavigate={actions.closeMobileMenu} />
          </nav>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-52 shrink-0 border-r border-line bg-app-elevated sm:block">
          <nav className="space-y-0.5 p-3">
            <NavLinks />
          </nav>
        </aside>

        <main className="flex-1 overflow-auto">
          {!onSettings && (
            <div className="mx-auto max-w-7xl px-4 pt-6 empty:hidden sm:px-6 lg:px-8">
              <SetupChecklist />
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
